export interface IOrgRole {
  id: string;
  name: string;
  is_active: boolean;
  organization_id: string;
  organization_name: string;
  created: Date;
  created_by: string;
  updated: Date;
}

export interface IOrgRoleDetail {
  role: {
    id: string;
    name: string;
    is_active: boolean;
    organization_id: string;
    organization_name: string;
    description: string;
  };
  role_permission: IPermission[];
}

export interface IOrgRoleForm {
  name: string;
  description: string;
  is_active: boolean;
  organization_id: string;
  permissions: IPermission[];
}

export interface IOrgIdsItem {
  id: string;
  name_en: string;
  name_vi: string;
}

export interface IOrgRoleIdsItem {
  id: string;
  name: string;
  organization_name: string;
}

export interface IPartnerIdsItem {
  id: string;
  name: string;
}

export interface IOrgEventIdsItem {
  id: string;
  name_en: string;
  name_vi: string;
  event_type: "sports" | "attractions";
  currency: string;
  organization_id: string;
}

export interface IOrgItem {
  alias: string;
  contact_email: string;
  contact_phone: string;
  event_type: EventType[];
  created: string;
  id: string;
  is_active: boolean;
  name_en: string;
  name_vi: string;
}

export interface IOrgFilters {
  q: string;
  gte: Date;
  lte: Date;
  is_active: string;
  page: number;
}

export interface IOrgDetail {
  id: string;
  name_vi: string;
  name_en: string;
  introduction_vi: string;
  introduction_en: string;
  workspace_domain: string;
  landing_page: string;
  organization_slug: string;
  contact_email: string;
  contact_phone_area_code: string;
  contact_phone: string;
  homepage_position: number;
  cover_banner: string;
  square_promotion_banner: string;
  rectangular_promotion_banner: string;
  organization_logo: string;
  organization_banner: string;
  brand_logo: string;
  brand_color: string;
  country: string;
  is_active: boolean;
  alias: string;
  created: Date;
  event_type: EventType[];
  seller_company_name: string;
  seller_company_email: string;
  seller_company_phone: string;
  seller_company_phone_area_code: string;
  seller_company_tax: string;
  seller_place_of_issue: string;
  seller_business_address: string;
  seller_business_license: string[];
  seller_business_certificate_number: string;
  seller_business_issue_date: string | null;
  use_whitelabel_integration: boolean;
  whitelabel_domain: string;
  whitelabel_favicon: string;
  whitelabel_brand_logo_url: string;
  whitelabel_email_footer: string;
  use_whitelabel_signin_signup: boolean;
  use_whitelabel_orders: boolean;
  whitelabel_sender_email: string;
  whitelabel_email_footer_text_color: string;
  whitelabel_menu: {
    url: string;
    name_en: string;
    name_vi: string;
  }[];
  whitelabel_header_text_color: string;
  whitelabel_footer_color: string;
  whitelabel_footer_text_color: string;
  whitelabel_left_website_footer_content_vi: string;
  whitelabel_left_website_footer_content_en: string;
  whitelabel_right_website_footer_content_vi: string;
  whitelabel_right_website_footer_content_en: string;
  use_fipix_integration: boolean;
  fipix_domain: string;
  fipix_brand_logo_url: string;
  fipix_menu: {
    url: string;
    name_en: string;
    name_vi: string;
  }[];
  fipix_seo_title: string;
  fipix_seo_description: string;
  fipix_seo_og_image: string;
  fipix_favicon: string;
  fipix_background_color: string;
  fipix_second_background_color: string;
  fipix_main_text_color: string;
  fipix_header_background_color: string;
  fipix_menu_text_color: string;
  fipix_footer_html_vi: string;
  fipix_footer_html_en: string;
}

export interface ICreateOrgForm {
  organization: {
    name_en: string;
    name_vi: string;
    is_active: boolean;
    country: string;
    workspace_domain: string;
    landing_page: string;
    organization_slug: string;
    alias: string;
    contact_phone_area_code: string;
    contact_phone: string;
    contact_email: string;
    cover_banner: string;
    square_promotion_banner: string;
    rectangular_promotion_banner: string;
    organization_logo: string;
    organization_banner: string;
    event_type: EventType[];
    seller_company_name: string;
    seller_company_email: string;
    seller_company_phone: string;
    seller_company_phone_area_code: string;
    seller_company_tax: string;
    seller_place_of_issue: string;
    seller_business_address: string;
    seller_business_license: string[];
    seller_business_certificate_number: string;
    seller_business_issue_date: string | null;
    use_whitelabel_integration: boolean;
    whitelabel_domain: string;
    whitelabel_favicon: string;
    whitelabel_brand_logo_url: string;
    whitelabel_email_footer: string;
    use_whitelabel_signin_signup: boolean;
    use_whitelabel_orders: boolean;
    whitelabel_sender_email: string;
    whitelabel_email_footer_text_color: string;
    whitelabel_menu: {
      url: string;
      name_en: string;
      name_vi: string;
    }[];
    use_fipix_integration: boolean;
    fipix_domain: string;
    fipix_brand_logo_url: string;
    fipix_menu: {
      url: string;
      name_en: string;
      name_vi: string;
    }[];
    fipix_seo_title: string;
    fipix_seo_description: string;
    fipix_seo_og_image: string;
    fipix_favicon: string;
    fipix_background_color: string;
    fipix_second_background_color: string;
    fipix_main_text_color: string;
    fipix_header_background_color: string;
    fipix_menu_text_color: string;
    fipix_footer_html_vi: string;
    fipix_footer_html_en: string;
  };
  account: {
    email: string;
    first_name: string;
    last_name: string;
  };
  permissions: IPermission[];
}

export interface IEditOrgForm {
  name_en: string;
  name_vi: string;
  is_active: boolean;
  country: string;
  organization_slug: string;
  homepage_position: number;
  introduction_en: string;
  introduction_vi: string;
  contact_phone_area_code: string;
  contact_phone: string;
  contact_email: string;
  cover_banner: string;
  square_promotion_banner: string;
  rectangular_promotion_banner: string;
  organization_logo: string;
  organization_banner: string;
  brand_logo: string;
  brand_color: string;
  workspace_domain?: string;
  landing_page?: string;
  event_type: EventType[];
  seller_company_name: string;
  seller_company_email: string;
  seller_company_phone: string;
  seller_company_phone_area_code: string;
  seller_company_tax: string;
  seller_place_of_issue: string;
  seller_business_address: string;
  seller_business_license: string[];
  seller_business_certificate_number: string;
  seller_business_issue_date: string | null;
  use_whitelabel_integration: boolean;
  whitelabel_domain: string;
  whitelabel_favicon: string;
  whitelabel_brand_logo_url: string;
  whitelabel_email_footer: string;
  use_whitelabel_signin_signup: boolean;
  use_whitelabel_orders: boolean;
  whitelabel_sender_email: string;
  whitelabel_email_footer_text_color: string;
  whitelabel_menu: {
    url: string;
    name_en: string;
    name_vi: string;
  }[];
  whitelabel_header_text_color: string;
  whitelabel_footer_color: string;
  whitelabel_footer_text_color: string;
  whitelabel_left_website_footer_content_vi: string;
  whitelabel_left_website_footer_content_en: string;
  whitelabel_right_website_footer_content_vi: string;
  whitelabel_right_website_footer_content_en: string;
  use_fipix_integration: boolean;
  fipix_domain: string;
  fipix_brand_logo_url: string;
  fipix_menu: {
    url: string;
    name_en: string;
    name_vi: string;
  }[];
  fipix_seo_title: string;
  fipix_seo_description: string;
  fipix_seo_og_image: string;
  fipix_favicon: string;
  fipix_background_color: string;
  fipix_second_background_color: string;
  fipix_main_text_color: string;
  fipix_header_background_color: string;
  fipix_menu_text_color: string;
  fipix_footer_html_vi: string;
  fipix_footer_html_en: string;
}

export interface IPermission {
  resource: string;
  app: string;
  actions: PermissionAction[];
  name: string;
}
export interface IOrgAccountItem {
  id: string;
  role_id: string;
  first_name: string;
  last_name: string;
  role_name: string;
  organization_name: string;
  email: string;
  phone: string;
  phone_area_code: string;
  is_active: boolean;
  created: Date;
  created_by: string;
}

export interface IOrgAccountDetail {
  role: {
    user_id: string;
    role_id: string;
    first_name: string;
    last_name: string;
    role_name: string;
    organization_id: string;
    organization_name: string;
    email: string;
    phone: string;
    phone_area_code: string;
    is_active: boolean;
    event_ids: string[];
  };
}

export interface ICreateOrgAccountForm {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  organization_id: string;
  phone_area_code: string;
  phone: string;
  event_ids: string[];
  is_active: boolean;
}

export interface IFipixAccountForm {
  username: string;
  full_name: string;
  events: string[];
  is_active: boolean;
}

export interface IFipixAccountItem {
  id: string;
  full_name: string;
  username: string;
  created_at: string;
  created_by: string;
  is_active: boolean;
}

export interface IFipixAccountDetail {
  id: string;
  username: string;
  created_at: string;
  created_by: string;
  full_name: string;
  is_active: boolean;
  events: string[];
}

export interface IEditOrgAccountForm {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  phone_area_code: string;
  phone: string;
  event_ids: string[];
  is_active: boolean;
}

export interface IUserData {
  name?: string;
  avatar?: string;
  token: string;
  email: string;
  type: UserType;
  permissions: IUserPermission[];
}

export interface IUserPermission {
  resource: string;
  app: string;
  action: PermissionAction[];
}

export interface IConditionItem {
  id: string;
  key: string;
  title_en: string;
  title_vi: string;
  is_active: boolean;
}

export interface IConditionDetail extends IConditionItem {
  content_en: string;
  content_vi: string;
  histories: {
    id: string;
    message: string;
    created_at: string;
    username: string;
  }[];
  updated_at: string;
}

export interface IEditConditionForm {
  key: string;
  title_en: string;
  title_vi: string;
  content_en: string;
  content_vi: string;
}

export interface IHomepageDetailData {
  "ads-banner": {
    "hero-banner": {
      id: string;
      data: IHeroBanner[];
    };
    "promotion-banner": {
      id: string;
      data: IBanner[];
    };
    "slide-banner": {
      id: string;
      data: IBanner[];
    };
    "top-banner": {
      id: string;
      data: ITopBanner[];
    };
    "affiliate-banner": {
      id: string;
      data: IBanner[];
    };
  };
  blog: {
    all: {
      id: string;
      data: IPostData[];
    };
    news: {
      id: string;
      data: IPostData[];
    };
    others: {
      id: string;
      data: IPostData[];
    };
    promo: {
      id: string;
      data: IPostData[];
    };
    blogs: {
      id: string;
      data: IPostData[];
    };
    health: {
      id: string;
      data: IPostData[];
    };
  };
  home: {
    "hot-event": {
      id: string;
      data: IPostData[];
    };
    organizer: {
      id: string;
      data: IPostData[];
    };
  };
}

export interface IEventIdsItem {
  id: string;
  name_en: string;
  name_vi: string;
  event_type: "sports" | "attractions";
  currency: string;
  organization_id: string;
}

export interface IBlogIdsItem {
  id: string;
  title_en: string;
  title_vi: string;
}

export interface IConditionIdsItem {
  id: number;
  note: string;
  is_active: number;
  created_at: string;
}

export interface IBanner {
  banner: string;
  mobile_banner?: string;
  open_url: string;
  position: number;
}

export interface ITopBanner {
  banner: string;
  mobile_banner: string;
  open_url: string;
  position: number;
}

export interface IHeroBanner {
  banner: string;
  type: "video" | "image";
  open_url: string;
}

export interface IPostData {
  id: string;
  name: string;
  position: number;
  is_active?: boolean;
}

export interface IOverviewData {
  stats: {
    total_discount_coupon: number;
    total_discount_promotion: number;
    total_grand_total: number;
    total_subtotal: number;
    total_total_item: number;
    total_attendant: number;
    total_orders: number;
  };
  growth_rates: {
    total_discount_coupon: number | "Infinity";
    total_discount_promotion: number | "Infinity";
    total_grand_total: number | "Infinity";
    total_subtotal: number | "Infinity";
    total_total_item: number | "Infinity";
    total_attendants: number | "Infinity";
    total_orders: number | "Infinity";
  };
}

export interface IGatewayFilterOptions {
  partner_codes: string[];
  gateway_names: string[];
}

export interface IOverviewEvents {
  event_id: string;
  subtotal: number;
  transactions: number;
  total_items: number;
  grand_total: number;
  discount_coupon: number;
  discount_promotion: number;
  attendants: number;
  event_name: string;
  selling_type: EventStatusTypes;
}

export interface IEventTickets {
  ticket_phase_name_en: string;
  ticket_phase_name_vi: string;
  total_discount_amount: number;
  quantity: number;
  price: number;
  gross_revenue: number;
  net_revenue: number;
  discount_amount: number;
  name_en: string;
  name_vi: string;
  order_type: string;
  sort_number: number;
}

export interface IEventTicketsIdsItem {
  product_name_en: string;
  product_name_vi: string;
}

export interface IEventGroup {
  attendant_quantity: number;
  attendant_paid_quantity: number;
  name: string;
  name_vi: string;
  name_en: string;
  payment_status: string;
  group_status: number;
  final_amount: number;
  total_amount: number;
  discount_amount: number;
  expired_date: string;
}

export interface IEventTicketPhaseIdsItem {
  id: string;
  key: string;
  name_vi: string;
  name_en: string;
  sell_from: string;
  sell_to: string;
  default: boolean;
}

export interface IGroupNameIdsItem {
  name_vi: string;
  name_en: string;
}

export interface ITShirtSize {
  t_shirt_size: string;
  category_name_en: string;
  category_name_vi: string;
  gender: string;
  quantity: number;
}

export interface IParticipantInfo {
  category_name_vi: string;
  category_name_en: string;
  quantity: number;
}
export interface IDistanceIdsItem {
  category_name_en: string;
  category_name_vi: string;
}

export interface IPendingPayment {
  gross_revenue: number;
  net_revenue: number;
  payment_method: string;
  total_order: number;
}

export interface ISportEventItem {
  id: string;
  banner_url: string;
  name: IMultilingualContent;
  start_date: string;
  end_date: string;
  open_registration_date: string;
  close_registration_date: string;
  event_type: string;
  location: string;
  status: EventStepStatus;
  current_step: number;
  created_at: string;
}

export interface IAttractionEventItem extends ISportEventItem {
  is_addition_done: boolean;
}

export interface IAttractionBuildEventPageForm {
  languages: SupportedLanguage[];
  platforms: TicketPlatform[];
  multilingual_content: {
    name: IMultilingualContent;
    place: IMultilingualContent;
    merchant_name: IMultilingualContent;
  };
  event_info: {
    banner_url: string;
    timezone: string;
    currency: SupportedCurrency;
    start_date: string;
    end_date: string;
    country: string;
    venue: string;
    event_slug: string;
    event_category_ids: any[];
    special_type: string;
    frequency_of_sell: FrequencyOfSellType;
    other_event_dates: string[];
    date_pricing: {
      enabled: boolean;
      holidays: { date: string; name: string }[];
    };
  };
  details: IEventSection[];
  ticket_on_sale: {
    open_registration_date: string;
    close_registration_date: string;
  };
  internal_setup: {
    merchant_id: string;
    apply_for_partners: any[];
    selling_max_qty: number;
    min_price: number;
    price_type: PriceType;
  };
  current_step: number;
  none?: boolean;
}

export interface IBuildEventPageForm {
  languages: SupportedLanguage[];
  platforms: TicketPlatform[];
  multilingual_content: {
    name: IMultilingualContent;
    place: IMultilingualContent;
    merchant_name: IMultilingualContent;
  };
  event_info: {
    banner_url: string;
    timezone: string;
    currency: SupportedCurrency;
    start_date: string;
    end_date: string;
    country: string;
    venue: string;
    event_slug: string;
    event_category_ids: any[];
    min_price: number;
    special_type: string;
    price_type: PriceType;
    selling_max_qty: number;
    date_pricing: {
      enabled: boolean;
      holidays: { date: string; name: string }[];
    };
  };
  details: IEventSection[];
  ticket_on_sale: {
    open_registration_date: string;
    close_registration_date: string;
  };
  internal_setup: {
    merchant_id: string;
    apply_for_partners: any[];
    mtb_id: string;
  };
  current_step: number;
  none?: boolean;
}

export interface IMultilingualContent {
  vi?: string;
  en?: string;
}

export interface IEventSection {
  title: IMultilingualContent;
  description: IMultilingualContent;
}

interface ICategory {
  id: string;
  cat_id: number;
  name_vi: string;
  name_en?: string;
}

export interface ISimpleEventDetail {
  id: string;
  banner_url: string;
  name: string;
  start_date: string;
  end_date: string;
  open_registration_date: string;
  close_registration_date: string;
  event_type: string;
  location: string;
  status: EventStepStatus;
  current_step: number;
  created_at: string;
  timezone: string;
  merchant_id: string;
  currency: SupportedCurrency;
  languages: SupportedLanguage[];
  is_payment_group: boolean;
  is_publish_all_ticket_phases: boolean;
  is_publish_all_ticket_phases_group: boolean;
  frequency_of_sell: FrequencyOfSellType;
  is_addition_done: boolean;
  date_pricing: {
    enabled: true;
    holidays: { date: string; name: string }[];
  };
}

export interface IEventDetail {
  id: string;
  banner_url: string;
  name: IMultilingualContent;
  place: IMultilingualContent;
  merchant_name: IMultilingualContent;
  start_date: string;
  end_date: string;
  open_registration_date: string;
  close_registration_date: string;
  event_type: string;
  location: string;
  status: EventStepStatus;
  current_step: number;
  created_at: string;
  languages: SupportedLanguage[];
  timezone: string;
  name_en: string;
  name_vi: string;
  selling_type: string;
  is_published: boolean;
  content_version: number;
  merchant_id: string;
  currency: SupportedCurrency;
  country: string;
  venue: string;
  event_slug: string;
  event_category_ids: string[];
  min_price: number;
  special_type: string;
  apply_for_partners: string[];
  mtb_id: string;
  selling_max_qty: number;
  event_is_active: boolean;
  details: IEventSection[];
  place_en: string;
  place_vi: string;
  frequency_of_sell?: FrequencyOfSellType;
  price_type?: PriceType;
  other_event_dates: string[];
  platforms: TicketPlatform[];
  date_pricing: {
    enabled: true;
    holidays: { date: string; name: string }[];
  };
}
export interface IHistoryLogItem {
  id: string;
  message: string;
  source: string;
  module: string;
  service: string;
  data: {
    [x: string]: {
      from: string;
      to: string;
    };
  };
  username: string;
  created_at: string;
}

export interface IAttendantTemplateItem {
  id?: string;
  event_id?: string;
  attendant_template_name: string;
  data: IAttendantTemplateData[];
}

export interface IAttendantTemplateData {
  fields: IAttendantTemplateInputItem[];
  group: ITemplateGroupItem;
}

export interface ITemplateGroupItem {
  group_key: string;
  label: IMultilingualContent;
}

export interface IAttendantTemplateForm {
  languages: SupportedLanguage[];
  event_id: string;
  templates: IAttendantTemplateItem[];
}

export interface IAdditionalSetupData {
  id: string;
  languages: SupportedLanguage[];
  timezone: string;
  banner_url: string;
  start_date: string;
  end_date: string;
  selling_type: string;
  close_registration_date: string;
  open_registration_date: string;
  event_type: string;
  location: string;
  is_published: string;
  current_step: string;
  created_at: string;
  content_version: string;
  merchant_id: string;
  end_change_distance_date: string | null;
  end_change_info_date: string | null;
  hold_ticket_time: number;
  ignore_payment_methods: PaymentMethodKey[];
  is_change_distance: boolean;
  is_change_registration: boolean;
  is_have_participant_list: boolean;
  is_hide_vat: boolean;
  display_group_button: boolean;
  is_transfer_ticket: boolean;
  max_number_of_times_to_change_distance: number;
  max_number_of_times_to_transfer_ticket: number;
  max_number_of_times_to_update_information: number;
  max_tickets_to_buy: number | null;
  order_max_qty_ticket: number;
  order_min_qty_ticket: number;
  payment_gateway_promotion_id: string;
  start_change_distance_date: string | null;
  start_change_info_date: string | null;
  transfer_ticket_fee: number;
  change_distance_fee: number;
  end_transfer_ticket_date: string | null;
  start_transfer_ticket_date: string | null;
  event_term_url: IMultilingualContent;
  note_individual_ticket_purchase_flow: IMultilingualContent;
  note_group_ticket_purchase_flow: IMultilingualContent;
  name: IMultilingualContent;
  is_ignore_participant_step: boolean;
  is_send_qr_code: boolean;
  special_template: string;
  seat_map_url: string;
  qr_code_type: QRCodeType | null;
  show_bib: boolean;
  is_display_on_the_organization_detail_page: boolean;
  private_flow?: {
    is_use: boolean;
    verify_type: PrivateFlowVerifyType | string;
    usage_count: number;
    email_domain: string;
    sync_enabled: boolean;
    passcode_source_event_ids: string[];
  };
  has_addon: boolean;
  use_pass_tool: boolean;
  use_authentication: boolean;
  legal_info: {
    company_name: string;
    address: string;
    tax_code: string;
    phone: string;
    representative: string;
    position: string;
    account_name: string;
    account_number: string;
    bank: string;
    contract_number: string;
    service_fee: number | any;
    vat: number | any;
  };
  is_einvoice_enabled?: boolean;
  label_event_on_einvoice?: string;
  is_static_html: boolean;
  static_html_content: string;
  order_code_prefix: string | null;
}

export interface IAttractionAddtionalSetupForm {
  basic_information: {
    is_ignore_participant_step: boolean;
    is_hide_vat: boolean;
    is_send_qr_code: boolean;
    event_term_url: IMultilingualContent;
    hold_ticket_time: number;
    max_tickets_to_buy: number | null;
    order_min_qty_ticket: number;
    order_max_qty_ticket: number;
    note_individual_ticket_purchase_flow: IMultilingualContent;
    seat_map_url: string;
    special_template: string;
    qr_code_type: QRCodeType | null;
    use_pass_tool: boolean;
    use_authentication: boolean;
    is_static_html: boolean;
    static_html_content: string;
    is_display_on_the_organization_detail_page: boolean;
  };
  payment_method: {
    ignore_payment_methods: PaymentMethodKey[];
    payment_gateway_promotion_id: string;
    order_code_prefix: string | null;
  };
  private_flow: {
    is_use: boolean;
    verify_type: PrivateFlowVerifyType | string;
    usage_count: number;
    email_domain: string;
    sync_enabled: boolean;
    passcode_source_event_ids: string[];
  };
  legal_info: {
    company_name: string;
    address: string;
    tax_code: string;
    phone: string;
    representative: string;
    position: string;
    account_name: string;
    account_number: string;
    bank: string;
    contract_number: string;
    service_fee: number;
    vat: number;
  };
  einvoice: {
    label_event_on_einvoice: string;
    is_einvoice_enabled: boolean;
  }
  none?: boolean;
}

export interface IAdditionalSetupForm {
  basic_information: {
    is_have_participant_list: boolean;
    is_hide_vat: boolean;
    display_group_button: boolean;
    show_bib: boolean;
    event_term_url: IMultilingualContent;
    hold_ticket_time: number;
    max_tickets_to_buy: number | null;
    order_min_qty_ticket: number;
    order_max_qty_ticket: number;
    note_individual_ticket_purchase_flow: IMultilingualContent;
    note_group_ticket_purchase_flow: IMultilingualContent;
    is_display_on_the_organization_detail_page: boolean;
    has_addon: boolean;
    use_pass_tool: boolean;
    use_authentication: boolean;
    is_static_html: boolean;
    static_html_content: string;
  };
  change_registration_information: {
    is_change_registration: boolean;
    start_change_info_date: string | null;
    end_change_info_date: string | null;
    max_number_of_times_to_update_information: number;
  };
  change_distance: {
    is_change_distance: boolean;
    start_change_distance_date: string | null;
    end_change_distance_date: string | null;
    max_number_of_times_to_change_distance: number;
    change_distance_fee: number;
  };
  transfer_ticket: {
    is_transfer_ticket: boolean;
    start_transfer_ticket_date: string | null;
    end_transfer_ticket_date: string | null;
    max_number_of_times_to_transfer_ticket: number;
    transfer_ticket_fee: number;
  };
  payment_method: {
    ignore_payment_methods: PaymentMethodKey[];
    payment_gateway_promotion_id: string;
    order_code_prefix: string | null; 
  };
  private_flow: {
    is_use: boolean;
    verify_type: PrivateFlowVerifyType | string;
    usage_count: number;
    email_domain: string;
    sync_enabled: boolean;
    passcode_source_event_ids: string[];
  };
  legal_info: {
    company_name: string;
    address: string;
    tax_code: string;
    phone: string;
    representative: string;
    position: string;
    account_name: string;
    account_number: string;
    bank: string;
    contract_number: string;
    service_fee: number;
    vat: number;
  };
  einvoice: {
    label_event_on_einvoice: string;
    is_einvoice_enabled: boolean;
  }
  none?: boolean;
}
export interface IAttendantTemplateInputItem {
  label: IMultilingualContent;
  sub_label: IMultilingualContent;
  sub_label_url: string;
  type: InputType;
  key: string;
  require: boolean;
  show: boolean;
  object?: FieldObjectType;
  is_lock_update_data: boolean;
  validation: ValidationType;
  option:
    | any
    | {
        min?: number;
        max?: number;
        [x: string]: any;
      }
    | IFieldOptionArrItem[]
    | null;
  [x: string]: any;
}

export interface ITicketPhasesForm {
  ticket_phases: Phase[];
  tickets: ITicketItem[];
  group_tickets: ITicketItem[];
  none?: any;
}

export interface IUsuallyTypeTicketForm {
  showtimes: ShowTime[];
  tickets: ITicketItem[];
}

export interface ITicketsResponseData extends ITicketPhasesForm {
  event_id: string;
  languages: SupportedLanguage[];
}

export interface INoPhaseTicketItem extends INoPhaseTicketForm {
  id: string;
}
export interface INoPhaseTicketForm {
  category: IMultilingualContent;
  type_name: IMultilingualContent;
  short_description: IMultilingualContent;
  selling_min_qty: number;
  selling_max_qty: number;
  selling_type: string;
  price_type: PriceType;
  original_price: number;
  price: number;
  sell_from: string;
  sell_to: string;
  attendant_template: {
    template_id: string;
    name: IMultilingualContent;
  }[];
  hide_in_ui: boolean;
  is_package?: boolean;
  is_accept_edit_participant_info?: boolean;
  is_accept_upgrade_ticket?: boolean;
  is_accept_transfer_ticket?: boolean;
  is_soldout: boolean;
  is_active: boolean;
  ticket_phase: string;
  product_type: ProductType;
  total_for_sell?: number | null;
  is_use_private_flow: boolean;
  is_use_actiup_private_flow?: boolean;
}

export interface IAttractionNoPhaseTicketForm extends INoPhaseTicketForm {
  is_auto_seating: boolean;
  start_seat_number?: number | null;
  auto_seating_leading_zero_spaces?: number | null;
  auto_seating_add_characters: boolean;
  auto_seating_characters: string;
  auto_seating_character_position: string;
}

export interface ISeatmapNoParentTicketForm extends IAttractionNoPhaseTicketForm {
  layout_type: SeatLayoutType;
  seat_layout_id: string | null;
  category_color: string | null;
}

export interface ISeatmapTicketsForm {
  tickets: ITicketItem[];
}

export interface IAttractionNoShowTimeTicketForm extends INoPhaseTicketForm {
  selling_days: string[];
  weekly_event_days: string[];
  sold_out_before: string;
  detailed_ticket_information: IMultilingualContent;
  pricing_by_date_type?: {
    weekend: { price: number; original_price: number };
    holiday: { price: number; original_price: number };
  };
}

export interface IPhaseTicketItem extends ITicketItem {}
export interface ITicketItem {
  ticket: IParentTicketItem | null;
  sub_tickets: ISubTicketItem[];
}

export interface IParentTicketItem extends IParentTicketForm {
  id: string;
}
export interface ISubTicketItem extends ISubTicketForm {
  id: string;
  category?: IMultilingualContent;
  selling_min_qty?: number;
  selling_max_qty?: number;
  selling_type?: string;
  is_package?: boolean;
  attendant_template?: {
    template_id: string;
    name: IMultilingualContent;
  }[];
  product_type?: ProductType;
  is_auto_seating: boolean;
  start_seat_number?: number | null;
  auto_seating_leading_zero_spaces?: number | null;
  auto_seating_add_characters: boolean;
  auto_seating_characters: string;
  auto_seating_character_position: string;
  selling_days?: string[];
  weekly_event_days?: string[];
  sold_out_before?: string;
  detailed_ticket_information?: IMultilingualContent;
  showtime?: string;
  layout_type?: SeatLayoutType;
  seat_layout_id?: string | null;
  category_color?: string | null;
  is_use_private_flow: boolean;
  is_use_actiup_private_flow?: boolean;
}

export interface ISeatLayoutDropdownItem {
  id: string;
  layout_name: string;
}

export interface IPhaseTicketForm {
  ticket: IParentTicketForm;
  sub_ticket: ISubTicketForm;
}

export interface IAttractionShowtimeTicketForm {
  ticket: IParentTicketForm;
  sub_ticket: ISubTicketForm & {
    selling_days: string[];
    weekly_event_days: string[];
    sold_out_before: string;
    detailed_ticket_information: IMultilingualContent;
    showtime: string;
    pricing_by_date_type?: {
      weekend: { price: number; original_price: number };
      holiday: { price: number; original_price: number };
    };
  };
}

export interface IParentTicketForm {
  category: IMultilingualContent;
  selling_min_qty: number;
  selling_max_qty: number;
  selling_type: string;
  is_package?: boolean;
  attendant_template: {
    template_id: string;
    name: IMultilingualContent;
  }[];
  product_type: ProductType;
  is_use_private_flow: boolean;
  is_use_actiup_private_flow?: boolean;
}

export interface ISubTicketForm {
  price_type: PriceType;
  type_name: IMultilingualContent;
  short_description: IMultilingualContent;
  original_price: number;
  price: number;
  sell_from: string;
  sell_to: string;
  hide_in_ui: boolean;
  is_accept_edit_participant_info?: boolean;
  is_accept_upgrade_ticket?: boolean;
  is_accept_transfer_ticket?: boolean;
  is_soldout: boolean;
  is_active: boolean;
  ticket_phase: string;
  total_for_sell?: number | null;
  pricing_by_date_type?: {
    weekend: { price: number; original_price: number };
    holiday: { price: number; original_price: number };
  };
}

export interface ISupportCategoryItem {
  id: string;
  name_vi: string;
  name_en: string;
  order_number: number;
  slug: string;
  username: string;
  is_active: boolean;
  number_of_qa: number;
}

export interface ISupportCateogryForm {
  name_en: string;
  name_vi: string;
  order_number: number;
  is_active: boolean;
}

export interface ISupportQAItem {
  id: string;
  title_vi: string;
  title_en: string;
  support_center_category_id: string;
  support_center_category_name: string;
  total_views: number;
  total_likes: number;
  total_dislikes: number;
  is_active: boolean;
}

export interface ISupportQADetail extends ISupportQAItem {
  detail_en: string;
  detail_vi: string;
  slug: string;
}

export interface IContactItem {
  id: string;
  support_center_category_id: string;
  support_center_category_name_vi: string;
  support_center_category_name_en: string;
  email: string;
  phone: string;
  phone_area_code: string;
  full_name: string;
  status: ContactStatus;
  description: string;
  created_at: string;
}
export interface ISupportQAForm {
  slug: string;
  title_vi: string;
  title_en: string;
  support_center_category_id: string;
  detail_en: string;
  detail_vi: string;
  is_active: boolean;
}

export interface IBlogSubCategoryItem {
  id: string;
  category: string;
  name_vi: string;
  name_en: string;
  is_active: boolean;
}

export interface IBlogSubCategoryForm {
  name_en: string;
  name_vi: string;
  category: string;
  is_active: boolean;
}

export interface IBlogItem {
  id: string;
  title_vi: string;
  title_en: string;
  slug: string;
  publish_at: string;
  category: string;
  sub_category_id: string;
  sub_category_name_vi: string;
  sub_category_name_en: string;
  is_hot_position: boolean;
  is_active: boolean;
}

export interface IBlogDetail extends IBlogItem {
  tags: string[];
  banner_url: string;
  related_blog_ids: string[];
  related_blogs: IRelatedBlogItem[];
  short_description_vi: string;
  short_description_en: string;
  content_vi: string;
  content_en: string;
}

export interface IRelatedBlogItem {
  id: string;
  title_vi: string;
  title_en: string;
}

export interface IBlogForm {
  title_vi: string;
  title_en: string;
  slug: string;
  publish_at: string;
  category: string;
  sub_category_id: string | null;
  tags: string[];
  banner_url: string;
  related_blog_ids: string[];
  short_description_vi: string;
  short_description_en: string;
  content_vi: string;
  content_en: string;
  is_hot_position: boolean;
  is_active: boolean;
}

export interface IFieldOption {}
export interface IFieldOptionArrItem {
  key?: string;
  label?: IMultilingualContent;
}

export interface Phase {
  name: {
    en: string;
    vi: string;
  };
  is_active: boolean;
  hide_in_ui: boolean;
  read_only?: boolean;
  id?: string;
  key?: string;
}

export interface ShowTime {
  time: string;
  is_active: boolean;
  hide_in_ui: boolean;
  id: string;
  created_at: string;
  updated_at: string;
}

export interface IMKTBannerDetail {
  sport_list: IMKTBanner;
  attraction_list: IMKTBanner;
  transport_station: IMKTBanner;
  blogs_home: IMKTBanner;
  blogs_detail: IMKTBanner;
  workspace: IMKTBanner;
  popup_homepage: IMKTBanner;
  popup_payment_status: IMKTBanner;
  homepage_coupon: IMKTBanner;
}

export interface IMKTBanner {
  id: string;
  type: string;
  data: IBanner[];
}

export interface ISupportCategoryIds {
  id: string;
  name_en: string;
  name_vi: string;
}

export interface IFipixEventItem {
  name_vi: string;
  name_en: string;
  banner_url: string;
  id: string;
  organization_id: string | null;
  slug: string;
  start_date: string;
  is_active: boolean;
  hidden: boolean;
}

export interface IFipixEventDetail extends IFipixEventItem {
  tags: string[];
  number_image: number;
  logo_top_left: string;
  logo_top_right: string;
  logo_bottom_left: string;
  logo_bottom_right: string;
  has_bib_recognition: boolean;
  has_face_recognition: boolean;
  show_all_images: boolean;
}

export interface IFipixEventForm {
  name_vi: string;
  name_en: string;
  banner_url: string;
  organization_id: string | null;
  start_date: string;
  slug?: string;
  is_active: boolean;
  hidden: boolean;
  has_bib_recognition: boolean;
  has_face_recognition: boolean;
  show_all_images: boolean;
}

export interface IOrderItem {
  id: string;
  code: string;
  group_name: string;
  buyer_name: string;
  buyer_phone: string;
  buyer_email: string;
  total_item: number;
  payment_status: number;
  coupon_codes: string[];
  item_description_vi: string;
  item_description_en: string;
  event_name_vi: string;
  event_name_en: string;
  ticket_use_for_date: string;
  is_vat: boolean;
  payment_method: string;
  event_id: string;
  order_status: OrderStatusKey;
  order_type: string;
  created_at: string;
  canceled_at: string;
  purchased_at: string;
  ticket_platform: string;
  showtime: string;
}

export interface IOrderDetail {
  id: string;
  code: string;
  order_type: string;
  order_status: OrderStatusKey;
  created_at: string;
  canceled_at: string;
  purchased_at: string;
  total_item: number;
  event_name_vi: string;
  event_name_en: string;
  payment_status: number;
  subtotal: number;
  discount_coupon: number;
  discount_promotion: number;
  total_fee: number;
  grand_total: number;
  ref_code: string;
  expired_payment_at: string;
  total_attendant: number;
  group_name: string;
  merchant_id: string;
  is_vat: boolean;
  auth_identifier: string;
  transaction: {
    id: string;
    parent_payment_method: string;
    selected_payment_method: string;
    reason_code: number;
    reference_number: string;
    card: {
      card_number: string;
      card_type: string;
      sub_card_type: string;
    };
    bank: {
      bank_id: number;
      bank_name_vi: string;
      bank_name_en: string;
    };
  } | null;
  organization: {
    id: string;
    name_vi: string;
    name_en: string;
  } | null;
  is_sent_email_success: boolean;
  is_sent_sms_success: boolean;
  payment_url: string;
  tax: {
    company_name: string;
    company_address: string;
    company_tax_code: string;
    recipient_name: string;
    recipient_phone: string;
    recipient_phone_area_code: string;
    recipient_address: string;
  } | null;
  buyer: {
    customer_code: string;
    name: string;
    phone: string;
    email: string;
    phone_area_code: string;
  };
  cart_id: string;
  updated_at: string;
  event_frequency_of_sell: FrequencyOfSellType;
  event_type: "sports" | "attractions";
  item_description_vi: string;
  item_description_en: string;
  rates?: IExchangeRateItem;
  ticket_platform: string;
  showtime: string;
  ticket_use_for_date: string;
  currency: string;
  addons?: IOrderAddonItem[];
}

export interface IEditOrderBuyerForm {
  buyer_name: string;
  buyer_phone: string;
  buyer_email: string;
  buyer_phone_area_code: string;
}

export interface IOrderVATForm {
  company_name: string;
  company_address: string;
  company_tax_code: string;
  recipient_name: string;
  recipient_phone: string;
  recipient_phone_area_code: string;
  recipient_address: string;
}

export interface IProductItem {
  product_id: string;
  product_name_en: string;
  product_name_vi: string;
  product_type: ProductType;
  product_sku: string;
  quantity: number;
  product_price: number;
  subtotal: number;
  discount_amount: number;
  grand_total: number;
  coupon: string;
}

export interface IOrderAddonItem {
  id: number;
  participant_id: string;
  addon_id: string;
  variant_id: string | null;
  addon_name: string;
  variant_label: string | null;
  qty: number;
  unit_price: number;
  subtotal: number;
  note: string | null;
}

export interface IOrderAttendantItem {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_area_code: string;
  ticket_no: any;
  participant_id: string;
  is_sent_email_success: boolean;
}
export interface IAttractionQRCodeItem {
  id: string;
  code: string;
  ticket?: {
    name_en: string;
    name_vi: string;
  };
  event?: {
    name_en: string;
    name_vi: string;
  };
  status: AttractionQRCodeSendingStatus;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface IHomepageCouponItem {
  id: string;
  coupon_code: string;
  title_en: string;
  content_en: string;
  is_active: boolean;
  is_sold_out: boolean;
}

export interface IHomepageCouponDetail {
  id: string;
  coupon_code: string;
  title_en: string;
  title_vi: string;
  content_en: string;
  content_vi: string;
  position: number | null;
  is_active: boolean;
  is_sold_out: boolean;
}

export interface IHomepageCouponForm {
  coupon_code: string;
  title_vi: string;
  title_en: string;
  content_vi: string;
  content_en: string;
  position: number | null;
  is_sold_out: boolean;
  is_active: boolean;
}

export interface IAttractionQRCodeDetail extends IAttractionQRCodeItem {
  updated_at: string;
}

export interface IAttractionQRCodeForm {
  event_id: string;
  product_id: string;
  is_active: boolean;
  codes: string;
}

export interface IPickupAccountItem {
  id: string;
  event_id: string;
  event_name: string;
  username: string;
  role_name: PickupAccountType;
  is_active: boolean;
  created_at: string;
  created_by: string;
  pickup_type?: PickupType;
  name?: string;
  category?: string[];
  is_zone: boolean;
}

export interface IPickupAccountDetail extends IPickupAccountItem {
  is_editable: boolean;
  logged_in?: boolean;
  password: string;
}

export interface IPickupAccountForm {
  role_name: string;
  event_id: string;
  is_active: boolean;
  username: string;
  password: string;
  is_editable?: boolean;
  name?: string;
  category?: string[];
  pickup_type?: PickupType;
  is_zone: boolean;
}

export interface IPickupEventItem {
  id: string;
  name: string;
  pickup: boolean;
  start_date: string;
}

export interface IPickupEventDetail {
  id: string;
  name: string;
  pickup: boolean;
  online_checked: boolean;
  logo: string;
  ewaiver_term: string;
  event_type: string;
  print_stamp: boolean;
  start_date: string;
  pickup_sign_required: boolean;
  map_bib_onsite: boolean;
}

export interface IPickupEventForm {
  pickup: boolean;
  logo: string;
  ewaiver_term: string | null;
  print_stamp: boolean | null;
  pickup_sign_required: boolean | null;
  map_bib_onsite: boolean | null;
}
export interface IExchangeRateItem {
  base: string;
  date: string;
  rates?: {
    [x: string]: number;
  };
}

export interface ISeatLayoutInfoForm {
  layout_name: string;
  row_name_type: "alphabet" | "number";
  row_name_position:
    | "left_top_down"
    | "left_bottom_up"
    | "right_top_down"
    | "right_bottom_up"
    | "right_bottom_up"
    | "top_left"
    | "top_right"
    | "bottom_left"
    | "bottom_right";
  number_of_columns: number;
  number_of_rows: number;
}

export interface ISeatLayoutForm extends ISeatLayoutInfoForm {
  seats: SeatItem[];
}

export interface ISeatLayoutData extends ISeatLayoutForm {
  id: string;
  event_id: string;
  product_id?: string;
}

export interface SeatItem {
  id?: string;
  row_index: number;
  column_index: number;
  status: SeatLayoutStatus;
  walkway: string[];
}

export interface IAttractionParticipantItem {
  id: string;
  full_name: string;
  category_vi: string;
  category_en: string;
  email: string;
  phone: string;
  phone_area_code: string;
  event_id: string;
  event_name_vi: string;
  event_name_en: string;
  is_active: boolean;
  parent_ref_id: string;
  ticket_no: string;
  seat_number: string;
  note: string;
  created_at: string;
  source: ParticipantImportSource;
}

export interface ISportParticipantItem {
  participant_id: string;
  full_name: string;
  category_name_vi: string;
  category_name_en: string;
  bib: any;
  email: string;
  phone: string;
  phone_area_code: string;
  id_card: string;
  event_id: string;
  event_name_vi: string;
  event_name_en: string;
  is_active: boolean;
  parent_ref_id: string;
  online_checked: number;
  note: string;
  created_at: string;
  source: ParticipantImportSource;
}

export interface ISportParticipantDetail {
  event?: {
    event_id: string;
    event_name: string;
    event_name_vi: string;
    event_logo: string;
    created_at: string;
    position_order: number;
    updated_by: string;
    is_have_participant_list: boolean;
    is_have_online_checkin: boolean;
    start_change_info_date: string;
    end_change_info_date: string;
    start_checkin_online_date: string;
    end_checkin_online_date: string;
    start_date: string;
    end_date: string;
    event_banner: string;
    extra_config?: {
      sender: string;
      is_review: boolean;
      kyc_ocr_limit: number;
      map_bib_onsite: boolean;
      online_checkin?: {
        kyc_required: boolean;
        waiver_sign_required: boolean;
      };
      ewaiver_banner_url: string;
      send_bib_via_email: boolean;
      ewaiver_race_agenda: string;
      is_sent_to_authorizer: boolean;
      is_change_pickup_method: boolean;
      kyc_face_matching_limit: number;
      kyc_face_matching_ratio: number;
      is_change_authorized_info: boolean;
      ewaiver_step_to_pickup_bib: string;
      number_of_times_change_pickup: number;
      unique_auth_url_email_template: string;
      qr_code_force_send_email_template: string;
      online_checkin_success_email_template: string;
      number_of_times_update_authorized_info: number;
      online_checkin_authorizer_email_template: string;
      online_checkin_notify_change_pickup_method_template: string;
    };
    event_type: string;
    show_bib: boolean;
    organization_id: string;
    languages: SupportedLanguage[];
  };
  participant: {
    participant_id: string;
    is_active: boolean;
    customer_id: string;
    created_at: string;
    reference_code: string;
    parent_ref_id: string;
    bib: string;
    full_name: string;
    attendant_template_id: string;
    attendant_template_name: string;
    event_name_vi: string;
    event_name_en: string;
    ticket_name_en: string;
    ticket_name_vi: string;
    is_update_information: boolean;
    time_to_update_information: number;
    category_name_vi: string;
    category_name_en: string;
    is_change_distance: boolean;
    time_to_change_distance: number;
    is_transferred: boolean;
    online_checked: boolean;
    kyc_by_legal: boolean;
    ocr_time: number;
    name_pick: string;
    email_pick: string;
    num_of_times_change_pickup: number;
    waiver_sign_completed: boolean;
    kyc_completed: boolean;
    face_matching_time: number;
    phone_pick: string;
    id_card_pick: string;
    num_of_times_update_authorized_info: number;
    number_of_times_to_transfer_ticket: number;
    is_online_checkin: boolean;
    source: ParticipantImportSource;
    [x: string]: any;
  };
  attendant_template: IAttendantTemplate;
}

export interface IPickupParticipantItem {
  participant_id: string;
  full_name: string;
  category: string;
  bib: string;
  email: string;
  phone: string;
  id_card: string;
  pos: string;
  event_name: string;
  order_code: string;
}
export interface IPickupSportParticipantItem extends IPickupParticipantItem {}

export interface IPickupSportParticipantDetail {
  participant: ISportParticipantDetail["participant"] & {
    age: string;
    event_name: string;
    category: string;
    order_code: string;
    pickup_note?: string;
    pos: string;
    pick_time: string;
    signature: string;
    image_pick: string;
    self_pick: boolean;
  };
  attendant_template: IAttendantTemplate;
  event?: {
    id: string;
    name_en: string;
    name_vi: string;
    start_date: string;
    languages: SupportedLanguage[];
  };
}
export interface IPickupAttractionParticipantItem extends IPickupParticipantItem {
  ticket_no: string;
}

export interface IPickupAttractionParticipantDetail {
  participant: {
    id: string;
    event_name: string;
    event_id: string;
    category: string;
    pos: string;
    order_code: string;
    product_id: string;
    pick_time: string;
    seat_number: string;
    ticket_no: string;
    participant_id: string;
    last_name: string;
    first_name: string;
    product_name: string;
    phone_area_code: string;
    attendant_template_id: string;
    attendant_template_name: string;
    ticket_name_en: string;
    ticket_name_vi: string;
    full_name: string;
    pickup_note?: string;
    [x: string]: any;
  };
  attendant_template: IAttendantTemplate;
  event?: {
    id: string;
    name_en: string;
    name_vi: string;
    start_date: string;
    languages: SupportedLanguage[];
  };
}

export interface IParticipantIdsData {
  participant_ids: string[];
  total_participants: number;
}

export interface IAttractionParticipantDetail {
  participant: {
    id: string;
    customer_id: string;
    event_id: string;
    event_name_vi: string;
    event_name_en: string;
    ticket_no: string;
    seat_number: string;
    note: string;
    is_active: boolean;
    full_name: string;
    category_vi: string;
    category_en: string;
    email: string;
    phone: string;
    phone_area_code: string;
    parent_ref_id: string;
    created_at: string;
    reference_code: string;
    attendant_template_id: string;
    attendant_template_name: string;
    ticket_name_en: string;
    ticket_name_vi: string;
    age: number;
    product_id: string;
    ticket_use_for_date: string;
    showtime: string;
    [x: string]: any;
  };
  attendant_template: IAttendantTemplate;
  event: {
    event_id: string;
    event_name: string;
    event_name_vi: string;
    event_logo: string;
    created_at: string;
    position_order: number;
    updated_by: string;
    is_have_participant_list: number;
    is_have_online_checkin: number;
    start_change_info_date: string;
    end_change_info_date: string;
    start_checkin_online_date: string;
    end_checkin_online_date: string;
    start_date: string;
    end_date: string;
    event_banner: string;
    extra_config: any;
    event_type: string;
    show_bib: boolean;
    organization_id: any;
  };
}

export interface IAttendantTemplate {
  [key: string]: {
    group_key: string;
    label_vi: string;
    label_en: string;
    fields: IAttendantTemplateInputItem[];
  };
}
export interface IAttractionParticipantForm {
  note: string;
  is_active: boolean;
  attendant_template_id: string;
  category_en?: string;
  category_vi?: string;
  [x: string]: any;
}

export interface ISportParticipantForm {
  bib: string;
  is_active: boolean;
  attendant_template_id: string;
  online_checked: boolean;
  waiver_sign_completed: boolean;
  kyc_by_legal: boolean;
  kyc_completed: boolean;
  ocr_time: number;
  face_matching_time: number;
  name_pick: string;
  email_pick: string;
  phone_pick: string;
  id_card_pick: string;
  num_of_times_update_authorized_info: number;
  num_of_times_change_pickup: number;
  category: {
    [key: string]: string;
  };
  [x: string]: any;
}

export interface IPickupSportParticipantForm {
  bib: string;
  full_name: string;
  category: string;
  email_pick: string;
  name_pick: string;
  id_card_pick: string;
  phone_pick: string;
  self_pick: boolean;
  [x: string]: any;
}

export interface IPickupAttractionParticipantForm {
  full_name: string;
  category: string;
  [x: string]: any;
}

export interface IPasscodeItem {
  id: string;
  auth_identifier: string;
  event_id: string;
  private_flow_type?: PrivateFlowType;
  usage_count: number;
  is_active: boolean;
  created_at: string;
  created_by: string;
  event_name_vi: string;
  event_name_en: string;
  max_usage_count: number;
}

export interface IAdminAccountRoleItem {
  id: string;
  name: string;
  is_active: boolean;
  organization_id: string;
  organization_name: string;
  created: string;
  created_by: string;
  updated: string;
}

export interface IAdminAccountRoleDetail {
  role: {
    id: string;
    name: string;
    is_active: boolean;
    organization_id: string;
    organization_name: string;
    description: string;
  };
  role_permission: IPermission[];
}

export interface IAdminAccountRoleForm {
  is_active: boolean;
  name: string;
  description: string;
  permissions: IPermission[];
}

export interface IAdminAccountItem {
  id: string;
  first_name: string;
  last_name: string;
  role_name: string;
  organization_name: string;
  email: string;
  phone: string;
  phone_area_code: string;
  is_active: boolean;
  created: string;
  created_by: string;
}

export interface IAdminAccountDetail {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_area_code: string;
  is_active: boolean;
  role_id: string;
  role_name: string;
  organization_ids: string[];
  organization_mapping: Record<string, string[]>;
}

export interface IAdminAccountForm {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  phone_area_code: string;
  phone: string;
  is_active: boolean;
  organization_ids: string[];
  organization_mapping: Record<string, string[]>;
}

export interface ITicketByEventIdsItem {
  event_id: string;
  name_en: string;
  name_vi: string;
  is_published: boolean;
  tickets: {
    id: string;
    name_en: string;
    name_vi: string;
    hide_in_ui: boolean;
  }[];
}

export interface IPromotionEventIdsItem {
  id: string;
  name_en: string;
  name_vi: string;
  event_type: "sports" | "attractions";
  currency: string;
  has_quantity_discount: boolean;
}

export interface ICreateCouponForm {
  apply_to_product_type: ApplyToProductType;
  is_active: boolean;
  event_ids: string[];
  note: string;
  apply_from: string;
  apply_to: string;
  discount_type_display: DiscountType;
  discount_value: number | null;
  discount_value_usd: number | null;
  max_discount_value: number | null;
  max_discount_value_usd: number | null;
  apply_to_level: ApplyToLevel | "" | null;
  min_total: number | null;
  min_total_usd: number | null;
  apply_product_ids: string[];
  apply_max_item_on_cart_qty: number | null;
  coupons?: {
    is_active: boolean;
    create_type: CouponCreateType;
    code_info_list: {
      code: string | null;
      customer_info_value: string | null;
    }[];
    customer_info_type: CouponCustomerInfoType | "" | null;
    max_times_used: number;
    short_desc_vi: string;
    short_desc_en: string;
    displayed_on_actiup: boolean;
    displayed_on_whitelabel: boolean;
    generate_character_position: GenerateCharacterPosition | null;
    generate_characters: string | null;
    generate_character_length: number | null;
    generate_total_coupons: number | null;
  };
  apply_to_payment_methods: PaymentMethodKey[];
  apply_from_hour: string | null;
  apply_to_hour: string | null;
  max_times_used: number;
  max_times_used_per_day: number | null;
  max_times_used_per_account: number | null;
  special_discount: boolean;
  coupon_code: string | null;
  customer_code: string | null;
  quantity_discount_rules: {
    qty: number;
    discount_value: number | null;
    discount_value_usd: number | null;
    max_discount_value: number | null;
    max_discount_value_usd: number | null;
    discount_type: DiscountType;
    apply_to_level: ApplyToLevel | "" | null;
  }[];
  discount_type: DiscountType | null;
  campaign_code: string | null;
}

export interface ICreateCouponWithExistingConditionsForm {
  condition_id: string;
  coupons?: {
    is_active: boolean;
    create_type: CouponCreateType;
    code_info_list: {
      code: string | null;
      customer_info_value: string | null;
    }[];
    customer_info_type: CouponCustomerInfoType | "" | null;
    max_times_used: number;
    short_desc_vi: string;
    short_desc_en: string;
    displayed_on_actiup: boolean;
    displayed_on_whitelabel: boolean;
    generate_character_position: GenerateCharacterPosition | null;
    generate_characters: string | null;
    generate_character_length: number | null;
    generate_total_coupons: number | null;
  };
}

export interface IPromotionConditionDetail {
  id: number;
  apply_product_ids: string[];
  event_ids: string[];
  apply_to_payment_methods: PaymentMethodKey[];
  apply_to_level: ApplyToLevel;
  discount_type: DiscountType | null;
  discount_value: number;
  max_discount_value: number;
  discount_value_usd: number;
  max_discount_value_usd: number;
  apply_to_product_type: ApplyToProductType;
  apply_to_flows: string;
  apply_from_hour: string;
  apply_from: string;
  apply_to_hour: string;
  apply_to: string;
  apply_max_item_on_cart_qty: number;
  coupon_code: string;
  max_times_used: number;
  max_times_used_per_day: number;
  min_total: number;
  min_total_usd: number;
  quantity_discount_rules: {
    qty: number;
    discount_value: number | null;
    discount_value_usd: number | null;
    max_discount_value: number | null;
    max_discount_value_usd: number | null;
    discount_type: DiscountType;
    apply_to_level: ApplyToLevel | "" | null;
  }[];
  is_active: boolean;
  created_by: string;
  note: string;
  event_id: string;
  campaign_code: string;
  created_at: string;
  updated_at: string;
  max_times_used_per_account: number | null;
  discount_type_display: DiscountType;
  products: {
    event_id: string;
    name_en: string;
    name_vi: string;
    is_published: boolean;
    currency: string;
    tickets: {
      id: string;
      name_en: string;
      name_vi: string;
      hide_in_ui: boolean;
    }[];
    is_all_tickets_matched: boolean;
  }[];
  coupon_codes: { code: string; id: string }[];
  customer_code: string | null;
  special_discount: boolean;
}

export interface IPromotionConditionProduct {
  event_id: string;
  name_en: string;
  name_vi: string;
  is_published: boolean;
  tickets: {
    id: string;
    name_en: string;
    name_vi: string;
    hide_in_ui: boolean;
  }[];
  is_all_tickets_matched: boolean;
}

export interface IEditPromotionConditionForm {
  apply_to_product_type: ApplyToProductType;
  is_active: boolean;
  event_ids: string[];
  note: string;
  apply_from: string;
  apply_to: string;
  discount_type_display: DiscountType;
  discount_value: number | null;
  discount_value_usd: number | null;
  max_discount_value: number | null;
  max_discount_value_usd: number | null;
  apply_to_level: ApplyToLevel | "" | null;
  min_total: number | null;
  min_total_usd: number | null;
  apply_product_ids: string[];
  apply_max_item_on_cart_qty: number | null;
  apply_to_payment_methods: PaymentMethodKey[];
  apply_from_hour: string | null;
  apply_to_hour: string | null;
  max_times_used: number;
  max_times_used_per_day: number | null;
  max_times_used_per_account: number | null;
  special_discount: boolean;
  coupon_code: string | null;
  customer_code: string | null;
  quantity_discount_rules:
    | {
        qty: number;
        discount_value: number | null;
        discount_value_usd: number | null;
        max_discount_value: number | null;
        max_discount_value_usd: number | null;
        discount_type: DiscountType;
        apply_to_level: ApplyToLevel | "" | null;
      }[]
    | null;
  discount_type: DiscountType | null;
  campaign_code: string | null;
}

export interface IPromotionCouponItem {
  id: string;
  code: string;
  rule_id: number;
  note: string;
  discount_value: number;
  discount_value_usd: number;
  apply_to_product_type: ApplyToProductType;
  apply_from: string;
  apply_to: string;
  times_used: number;
  max_times_used: number;
  is_active: boolean;
  event_ids: string[];
  discount_type: DiscountType;
}

export interface IPromotionConditionItem {
  id: number;
  note: string;
  discount_type: DiscountType;
  apply_to_product_type: ApplyToProductType;
  apply_from: string;
  apply_to: string;
  is_active: boolean;
  event_ids: string[];
  discount_value: number;
  discount_value_usd: number;
}

export interface IPromotionCouponDetail {
  id: string;
  code: string;
  usage_count: number;
  max_times_used: number;
  condition: {
    id: number;
    apply_product_ids: string[];
    event_ids: string[];
    apply_to_payment_methods: PaymentMethodKey[];
    quantity_discount_rules: {
      qty: number;
      discount_value: number | null;
      discount_value_usd: number | null;
      max_discount_value: number | null;
      max_discount_value_usd: number | null;
      discount_type: DiscountType;
      apply_to_level: ApplyToLevel | "" | null;
    }[];
    apply_from: string;
    apply_to: string;
    created_at: string;
    updated_at: string;
    apply_to_level: ApplyToLevel;
    discount_type: DiscountType;
    discount_value: number;
    max_discount_value: number;
    discount_value_usd: number | null;
    max_discount_value_usd: number | null;
    apply_to_product_type: ApplyToProductType;
    apply_to_flows: string;
    apply_from_hour: string | null;
    apply_to_hour: string | null;
    apply_max_item_on_cart_qty: number | null;
    coupon_code: any;
    max_times_used: number;
    max_times_used_per_day: number | null;
    min_total: number | null;
    min_total_usd: number | null;
    is_active: boolean;
    created_by: string;
    note: string;
    event_id: string;
    campaign_code: string | null;
    max_times_used_per_account: number | null;
    discount_type_display: DiscountType;
  };
  customer_info_type: CouponCustomerInfoType | "" | null;
  customer_info_value: string | null;
  is_active: boolean;
  created_by: string;
  created_at: string;
  displayed_on_actiup: boolean;
  displayed_on_whitelabel: boolean;
  short_desc_vi: string;
  short_desc_en: string;
}

export interface IEditPromotionCouponForm {
  is_active: boolean;
  rule_id: number | null;
  customer_info_type: CouponCustomerInfoType | "" | null;
  customer_info_value: string | null;
  max_times_used: number;
  short_desc_vi: string;
  short_desc_en: string;
  displayed_on_actiup: boolean;
  displayed_on_whitelabel: boolean;
}

export interface IEInvoiceSetting {
  einvoice_enabled: boolean;
  sepay_invoice_series: string;
  sepay_provider_account_id: string;
  sepay_template_code: string;
  sepay_username: string;
}

export interface IEInvoiceSettingForm {
  einvoice_enabled: boolean;
  sepay_username: string;
  sepay_password: string;
  // sepay_provider_account_id: string;
  // sepay_template_code: string;
  // sepay_invoice_series: string;
}

export interface IEInvoiceBuyer {
  type: string;
  name: string;
  legal_name: string | null;
  tax_code: string | null;
  address: string;
  email: string;
  phone: string;
  buyer_code: string | null;
  national_id: string | null;
}

export interface IEInvoiceItem {
  reference_code: string;
  invoice_number: string;
  invoice_series: string;
  document_type: number;
  issued_date: string;
  pdf_url: string | null;
  xml_url: string | null;
  status: string;
  tax_authority_code: string | null;
  provider_status: string | null;
  payment_method: string;
  buyer: IEInvoiceBuyer;
  total_before_tax: number;
  tax_amount: number;
  total_amount: number;
  notes: string;
}

export interface IUploadImageData {
  url: string;
  files: IUploadFile[];
}

export interface IUploadFile extends File {
  preview: string;
}

export enum EventStatusTypes {
  SELLING = "on_selling",
  COMING = "coming_soon",
  CLOSED = "closed",
  UNKNOWN = "",
}

export enum GroupStatusTypes {
  CANCELED = 0,
  OPENING = 1,
  SENDPAYMENT = 2,
  CLOSED = 3,
  SUCCESS = 4,
  UNKNOWN = "",
}

export enum PermissionAction {
  CREATE = "create",
  EDIT = "edit",
  VIEW = "view",
  EXPORT = "export",
  DELETE = "delete",
}

export enum PermissionResource {
  ACCOUNT = "account",
  ROLE = "role",
  INFO = "info",
}

export enum EventType {
  SPORT = "sport",
  ATTRACTION = "attraction",
}

export enum DatePickerFilterOption {
  TODAY = "today",
  YESTERDAY = "yesterday",
  SEVENDAYS = "7days",
  THIRTYDAYS = "30days",
  DAYS = "days",
  WEEK = "week",
  MONTH = "month",
  QUARTER = "quarter",
  YEAR = "year",
}

export enum CreateEventStep {
  BUILD_EVENT_PAGE = 1,
  PARTICIPANT_TEMPLATE = 2,
  CREATE_TICKET_PHASE = 3,
  ADDITIONAL_SETUP = 4,
  PUBLISH = 5,
}

export enum SupportedLanguage {
  VI = "vi",
  EN = "en",
}

export enum SupportedCurrency {
  VND = "vnd",
  USD = "usd",
}

export enum InputType {
  TEXT = "text",
  SELECT = "select",
  DATE = "date",
  NUMBER = "number",
  DATETIME = "datetime",
  MULTI_SELECT = "multi_select",
  CHECKBOX = "checkbox",
  RADIO_BUTTON = "radio_button",
}

export enum EventStepStatus {
  PUBLISHED = "published",
  UNPUBLISHED = "un_published",
  DRAFT = "draft",
}

export enum ValidationType {
  LENGTH = "length",
  DOB = "dob",
  EMAIL = "email",
  PHONE = "phone",
  NONE = "",
}

export enum FieldObjectType {
  NATIONALITIES = "nationalities",
  COUNTRIES = "countries",
  CLUBS = "clubs",
  GENDERS = "genders",
  PROVINES = "provinces",
  BLOOD_TYPE = "blood_type",
  TSHIRT_SIZE = "tshirt_size",
  TSHIRT_SIZE_FINISHER = "t_shirt_size_finisher",
  RELATIONSHIP = "relationship",
  CUSTOM = "custom",
  NONE = "",
}

export enum PaymentMethodKey {
  ALL = "all",
  ATM = "atm",
  CREDIT = "credit",
  BANK_TRANSFER = "bank_transfer",
  COD = "cod",
  PAYOO = "vnpay",
  ZALOPAY = "zalopay",
  MOMO = "momo",
  VIETQR = "viet_qr",
  VNPAY = "vn_pay_b",
  BNPL = "bnpl",
  VIETTEL_MONEY_VIETQR = "viettel_money",
  VIETTEL_MONEY_ATM = "v_atm",
  VIETTEL_MONEY_CREDIT = "v_credit",
  PAYOO_ATM = "payoo_atm",
  PAYOO_CREDIT = "payoo_credit",
  ONEPAY_VIETQR = "onepay_viet_qr",
  STRIPE = "stripe",
}

export enum ReadonlyPaymentMethodkey {
  FREE = "free",
  VOUCHER = "voucher",
}

export enum OrderStatusKey {
  PENDING = "doi_thanh_toan",
  SUCCESSFUL = "dat_hang_thanh_cong",
  FAILED = "da_huy",
  REFUNDED = "hoan_tien",
  WAITING_FOR_REFUND = "cho_hoan_tien",
}

export enum PriceType {
  PAID = "paid",
  FREE = "free",
}

export enum ProductType {
  TICKET = "ticket",
  PAYMENT_GROUP = "payment_group",
}

export enum FrequencyOfSellType {
  ONCE = "once",
  USUALLY = "usually",
  SEATMAP = "seatmap",
}

export enum QRCodeType {
  GENERATE = "gen_code",
  IMPORT = "import_code",
}

export enum TicketType {
  INDIVIDUAL = "individual",
  GROUP = "group",
}

export enum ContactStatus {
  UNRESOLVED = "waiting",
  PROCESSING = "processing",
  RESOLVED = "done",
}

export enum OrderType {
  GROUP_BUY = "group_buy",
  ATTRACTIONS = "attractions",
  VIRTUAL_EVENT = "virtual_event",
  CHANGE_DISTANCE = "change_distance",
  TRANSFER_TICKET = "transfer_ticket",
}

export enum PaymentStatusType {
  NEW = 0,
  SUCCESS = 1,
  REVIEW = 2,
  FAIL = 3,
  REFUND = 4,
  REFUND_REVIEW = 5,
}

export enum AttractionQRCodeSendingStatus {
  CREATED = "new",
  WAITING = "waiting",
  SENT = "used",
}

export enum PickupAccountType {
  POS = "pos",
  ADMIN = "admin",
}

export enum PickupType {
  ALL = "all",
  SELF_PICK = "self-pick",
  GROUP_PICK = "group-pick",
}

export enum SeatLayoutStatus {
  AVAILABLE = "available",
  UNAVAILABLE = "unavailable",
  SOLD_OUT = "sold_out",
  INACTIVE = "inactive",
  HIDE = "hide",
  WALKWAY = "walkway",
  WALKWAY_ON_THE_LEFT = "walkway_on_the_left",
  WALKWAY_ON_THE_RIGHT = "walkway_on_the_right",
  WALKWAY_ABOVE = "walkway_above",
  WALKWAY_BELOW = "walkway_below",
  IN_CART = "in_cart",
  REMOVE = "remove",
}

export enum SeatLayoutPosition {
  LEFT_TOP_DOWN = "left_top_down",
  LEFT_BOTTOM_UP = "left_bottom_up",
  RIGHT_TOP_DOWN = "right_top_down",
  RIGHT_BOTTOM_UP = "right_bottom_up",
  TOP_LEFT = "top_left",
  TOP_RIGHT = "top_right",
  BOTTOM_LEFT = "bottom_left",
  BOTTOM_RIGHT = "bottom_right",
}

export enum SeatLayoutType {
  ZONE = "zone",
  SEAT = "seat",
}

export enum PrivateFlowVerifyType {
  PASSCODE = "passcode",
  SPECIFIC_EMAIL = "specific_email",
  DOMAIN_EMAIL = "domain_email",
  PASSCODE_AND_EMAIL = "passcode_and_email",
}

export enum PrivateFlowType {
  PRIVATE_FLOW = "private_flow",
  ACTIUP_PRIVATE_FLOW = "actiup_private_flow",
}

export enum UserType {
  ADMIN = "admin",
  WPS = "wps",
}

export enum ApplyToProductType {
  ALL = "all",
  INCLUDE = "include",
  EXCLUDE = "exclude",
}

export enum ApplyToLevel {
  TOTAL_ORDER = "cart",
  PER_ITEM = "item",
}

export enum DiscountType {
  FIX_PRICE = "fix_price",
  PERCENT = "percent",
  QUANTITY_DISCOUNT = "quantity_discount",
  GROUP_DISCOUNT = "group_discount",
  CAMPAIGN_DISCOUNT = "campaign_discount",
}

export enum CouponCreateType {
  MANUAL = "manual",
  GENERATE = "generate",
}

export enum CouponCustomerInfoType {
  EMAIL = "email",
  CODE = "code",
}

export enum GenerateCharacterPosition {
  PREFIX = "prefix",
  SUFFIX = "suffix",
}

export enum TicketPlatform {
  ACTIUP = "actiup",
  WHITELABEL = "white_label",
}

export enum ParticipantImportSource {
  SYSTEM = "system",
  IMPORT = "import"
}
export default interface UserInfoWPS {
  email: string;
  password: string;
  new_password: string;
  confirm_password: string;
}

export default interface AccountWPS {
  status: boolean;
  email: string;
  first_name: string;
  last_name: string;
  role_name: string;
  phone: string;
  event: string;
}

export default interface OrganizationWPS {
  alias: string;
  brand_color: string;
  brand_logo: string;
  country: string;
  cover_banner: string;
  introduction: {
    en: string;
    vi: string;
  };
  is_active: boolean;
  name: {
    en: string;
    vi: string;
  };
  organization_banner: string;
  organization_logo: string;
  rectangular_promotion_banner: string;
  square_promotion_banner: string;
  seller_business_address: string;
  seller_business_certificate_number: string;
  seller_business_issue_date: string;
  seller_business_license: string[];
  seller_company_email: string;
  seller_company_name: string;
  seller_company_phone: string;
  seller_company_phone_area_code: string;
  seller_company_tax: string;
  seller_place_of_issue: string;
}
